'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';
import MobileMenuToggle from './MobileMenuToggle';
import { useAuth } from '@/contexts/AuthContext';
import { ArrowRightOnRectangleIcon, UserCircleIcon } from '@heroicons/react/24/outline';

const ROLE_DASHBOARDS: Record<string, string> = {
  b2c_individual: '/b2c-dashboard',
  b2b_fleet: '/b2b-dashboard',
  operator: '/operator-dashboard',
  admin: '/admin-ops-portal',
};

export default function LandingHeader() {
  const { user, signOut, loading } = useAuth();
  const router = useRouter();
  const [signingOut, setSigningOut] = useState(false);

  const handleSignOut = async () => {
    setSigningOut(true);
    try {
      await signOut();
      router?.push('/');
      router?.refresh();
    } catch {
      // ignore
    } finally {
      setSigningOut(false);
    }
  };

  const dashboardHref = user
    ? ROLE_DASHBOARDS[user?.user_metadata?.role || 'b2c_individual'] || '/b2c-dashboard'
    : '/b2c-dashboard';

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-primary/95 backdrop-blur-md border-b border-white/10">
      <div className="max-w-screen-2xl mx-auto px-6 lg:px-8 xl:px-10 h-16 flex items-center justify-between">
        <Link href="/" className="flex items-center gap-2.5">
          <AppLogo size={36} />
          <span className="font-bold text-xl text-white tracking-tight">ChallanDesk</span>
        </Link>
        <nav className="hidden md:flex items-center gap-1">
          <Link href="/" className="px-3 py-2 text-sm font-medium text-slate-300 hover:text-white transition-colors rounded-lg hover:bg-white/10">
            Home
          </Link>
          <Link href="#features" className="px-3 py-2 text-sm font-medium text-slate-300 hover:text-white transition-colors rounded-lg hover:bg-white/10">
            Features
          </Link>
          <Link href="#pricing" className="px-3 py-2 text-sm font-medium text-slate-300 hover:text-white transition-colors rounded-lg hover:bg-white/10">
            Pricing
          </Link>
          <Link href="#faq" className="px-3 py-2 text-sm font-medium text-slate-300 hover:text-white transition-colors rounded-lg hover:bg-white/10">
            FAQ
          </Link>
          <Link href="/register?type=b2b" className="px-3 py-2 text-sm font-medium text-slate-300 hover:text-white transition-colors rounded-lg hover:bg-white/10">
            For Businesses
          </Link>
        </nav>
        <div className="hidden md:flex items-center gap-3">
          {loading ? (
            <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
          ) : user ? (
            <>
              <Link
                href={dashboardHref}
                className="flex items-center gap-1.5 px-4 py-2 text-sm font-semibold text-white border border-white/30 rounded-lg hover:bg-white/10 transition-all duration-150"
              >
                <UserCircleIcon className="w-4 h-4" />
                Dashboard
              </Link>
              <button
                onClick={handleSignOut}
                disabled={signingOut}
                className="flex items-center gap-1.5 px-4 py-2 text-sm font-semibold text-slate-300 hover:text-white rounded-lg hover:bg-white/10 transition-all duration-150"
              >
                <ArrowRightOnRectangleIcon className="w-4 h-4" />
                {signingOut ? 'Signing out...' : 'Sign Out'}
              </button>
            </>
          ) : (
            <>
              <Link href="/login" className="px-4 py-2 text-sm font-semibold text-white border border-white/30 rounded-lg hover:bg-white/10 transition-all duration-150">
                Login
              </Link>
              <Link href="/register" className="px-4 py-2 text-sm font-semibold bg-accent text-white rounded-lg hover:bg-blue-700 transition-all duration-150 active:scale-95">
                Sign Up Free
              </Link>
            </>
          )}
        </div>
        <MobileMenuToggle />
      </div>
    </header>
  );
}